// Generic model helpers for MonthlyHistoricalPivotTable consumers: rows config
// validation and per-month value cells. Not domain-specific; a consumer adds
// its own row types / cells on top (e.g. Helper/BatteryHealth/SohHistoryHelper).
//
// Rows config entry: { key, type, label, past?, future?, subPast?, subFuture?, format? }
//   past / future        field read on history / forecast months
//   subPast / subFuture  optional second-line field
//   format               see Helper/Formatters/PivotValueFormatter
// Month: { month: "YYYY-MM", kind: "history" | "forecast", status?, values? }

import { EMPTY_VALUE, formatPivotValue } from "../../../Helper/Formatters/PivotValueFormatter";

export const PIVOT_ROW_TYPES = ["status", "metric", "entities"];

const isPlainObject = (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value);

const nonEmptyString = (value) => typeof value === "string" && value.trim() !== "";

export const isForecastMonth = (month) =>
    month?.kind === "forecast" || month?.status === "forecast";

/**
 * Rows config: objects with a string key and a known type, duplicates by key
 * dropped, and only the FIRST "entities" row kept (entity rows are keyed by
 * entity, so a second one would collide). Order kept. Missing / empty ->
 * a copy of `defaults`.
 */
export const resolvePivotRows = (rows, defaults = []) => {
    const fallback = () => (Array.isArray(defaults) ? defaults.map((r) => ({ ...r })) : []);
    if (!Array.isArray(rows)) return fallback();
    const seen = new Set();
    let hasEntities = false;
    const cleaned = rows.filter((row) => {
        if (!isPlainObject(row) || !nonEmptyString(row.key) || !PIVOT_ROW_TYPES.includes(row.type)) return false;
        if (seen.has(row.key)) return false;
        if (row.type === "entities") {
            if (hasEntities) return false;
            hasEntities = true;
        }
        seen.add(row.key);
        return true;
    });
    return cleaned.length ? cleaned : fallback();
};

/** Field names a row reads for a month: past* on history, future* on forecast. */
export const getRowFields = (row, month) => {
    const forecast = isForecastMonth(month);
    return {
        field: forecast ? row?.future : row?.past,
        subField: forecast ? row?.subFuture : row?.subPast,
    };
};

/**
 * Value cell from a values object (month.values or an entity's month values).
 *   { kind: "value", text, subText, subField, isForecast }
 *   { kind: "empty", text: "—", ... } when the row has no field for this kind
 *   of month, or the value is missing / unusable.
 * subText is the raw second-line value as a string (not translated).
 */
export const getValueCell = (row, month, values) => {
    const forecast = isForecastMonth(month);
    const { field, subField } = getRowFields(row, month);
    const source = isPlainObject(values) ? values : {};
    const text = field ? formatPivotValue(source[field], row?.format) : EMPTY_VALUE;
    const rawSub = subField ? source[subField] : null;
    const subText = rawSub === null || rawSub === undefined || rawSub === "" ? null : String(rawSub);
    const empty = text === EMPTY_VALUE;
    return {
        kind: empty ? "empty" : "value",
        text,
        subText: empty ? null : subText,
        subField,
        isForecast: forecast,
    };
};

/** Metric row cell: one value per month from month.values. */
export const getMetricCell = (row, month) => getValueCell(row, month, month?.values);
